import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Business,
  Dashboard as DashboardIcon,
  Group,
  Assignment,
  Apartment,
  BarChart,
  CheckCircle,
  Block,
  Edit,
  Refresh,
  Add,
  Delete,
  Close as CloseIcon,
} from '@mui/icons-material';
import { ThemeProvider, createTheme, styled, useTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { getUsers, suspendUser, activateUser } from '../../services/users';
import { departmentService, roomService, orderService } from '../../services/ris';
import api from '../../services/api';

// Import existing clinical dashboards for the clinical section
import ClinicianDashboard from '../Clinician/Dashboard';
import RadiologistDashboard from '../Radiologist/Dashboard';

const DRAWER_WIDTH = 240;

const StyledDrawer = styled(Drawer)(() => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    background: '#0a1929',
    color: '#e2e8f0',
    borderRight: '1px solid rgba(255,255,255,0.06)',
  },
}));

// ─── Overview / Stats View ────────────────────────────────────────────────────
function OverviewView({ facility }) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const cardBorder = `1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'}`;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!facility) return;
    const facilityId = facility._id || facility;
    Promise.all([
      orderService.getStats({ facility: facilityId }).catch(() => null),
      api.get('/users', { params: { facilityId, limit: 1 } }).catch(() => null),
      api.get('/departments', { params: { facility: facilityId } }).catch(() => null),
    ]).then(([orderRes, userRes, deptRes]) => {
      setStats({
        totalOrders: orderRes?.data?.total || 0,
        activeOrders: orderRes?.data?.active || orderRes?.data?.byStatus?.NEW || 0,
        totalStaff: userRes?.data?.pagination?.total || 0,
        departments: deptRes?.data?.total || 0,
      });
      setLoading(false);
    });
  }, [facility]);

  const cards = [
    { label: 'Total Orders', value: stats?.totalOrders ?? '–', color: '#3b82f6', icon: <Assignment sx={{ fontSize: 18 }} /> },
    { label: 'Active Orders', value: stats?.activeOrders ?? '–', color: '#10b981', icon: <CheckCircle sx={{ fontSize: 18 }} /> },
    { label: 'Staff Members', value: stats?.totalStaff ?? '–', color: '#f59e0b', icon: <Group sx={{ fontSize: 18 }} /> },
    { label: 'Departments', value: stats?.departments ?? '–', color: '#8b5cf6', icon: <Apartment sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Box>
      <Box mb={1.5}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', lineHeight: 1.2, fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
          Facility Overview
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', fontFamily: '"Satoshi", sans-serif', color: textColorSecondary }}>
          High-level operational metrics and quick facility stats
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
      ) : (
        <Grid container spacing={1.5}>
          {cards.map((c) => (
            <Grid item xs={12} sm={6} md={3} key={c.label}>
              <Card sx={{ background: cardBg, borderRadius: '12px', border: cardBorder, boxShadow: 'none' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: c.color + (isDark ? '30' : '18'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.color }}>
                    {c.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: textColorPrimary, lineHeight: 1.1, fontSize: '1.25rem', fontFamily: '"Satoshi", sans-serif' }}>
                      {c.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: textColorSecondary, fontSize: '0.72rem', fontFamily: '"Satoshi", sans-serif' }}>
                      {c.label}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

// ─── Staff Management View ────────────────────────────────────────────────────
function StaffView({ facility }) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const cardBorder = `1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'}`;
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null, action: '' });

  const facilityId = facility?._id || facility;

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const data = await getUsers({ facilityId, limit: 100 });
      setUsers(data.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async () => {
    const { user, action } = confirmDialog;
    try {
      if (action === 'suspend') await suspendUser(user._id);
      else await activateUser(user._id);
      setConfirmDialog({ open: false, user: null, action: '' });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const filtered = users.filter(u =>
    `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const roleLabel = (r) => ({ clinician: 'Clinician', radiologist: 'Radiologist', technologist: 'Technologist', facility_admin: 'Facility Admin', admin: 'Admin' }[r] || r);

  return (
    <Box>
      <Box mb={1.5}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', lineHeight: 1.2, fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
          Staff Management
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', fontFamily: '"Satoshi", sans-serif', color: textColorSecondary }}>
          Manage user accounts, roles, and access status within this facility
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif' }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search staff…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          InputProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
        />
        <Button
          startIcon={<Refresh sx={{ fontSize: 16 }} />}
          variant="outlined"
          size="small"
          onClick={load}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontFamily: '"Satoshi", sans-serif',
            fontSize: '0.78rem',
            fontWeight: 600,
            borderColor,
            color: textColorPrimary,
            height: 32,
          }}
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
      ) : (
        <Card sx={{ background: cardBg, borderRadius: '12px', border: cardBorder, boxShadow: 'none' }}>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow key={u._id} hover>
                    <TableCell sx={{ py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#3b82f6', fontSize: 11, fontFamily: '"Satoshi", sans-serif' }}>
                          {u.firstName?.[0]}{u.lastName?.[0]}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
                          {u.firstName} {u.lastName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}><Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif', color: textColorSecondary }}>{u.email}</Typography></TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <Chip label={roleLabel(u.role)} size="small"
                        color={u.role === 'radiologist' ? 'primary' : u.role === 'facility_admin' ? 'warning' : 'default'}
                        sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, fontFamily: '"Satoshi", sans-serif' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <Chip
                        label={u.isActive ? 'Active' : 'Suspended'}
                        size="small"
                        color={u.isActive ? 'success' : 'error'}
                        sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, fontFamily: '"Satoshi", sans-serif' }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      {u.role !== 'admin' && u.role !== 'facility_admin' && (
                        u.isActive ? (
                          <Tooltip title="Suspend">
                            <IconButton size="small" color="error"
                              onClick={() => setConfirmDialog({ open: true, user: u, action: 'suspend' })}>
                              <Block sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activate">
                            <IconButton size="small" color="success"
                              onClick={() => setConfirmDialog({ open: true, user: u, action: 'activate' })}>
                              <CheckCircle sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: textColorSecondary, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif' }}>No staff found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, user: null, action: '' })}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '440px' },
            maxWidth: '460px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#F1F5F9' : '#0F172A',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider', fontSize: '1.05rem', fontWeight: 700 }}>
          {confirmDialog.action === 'suspend' ? 'Suspend' : 'Activate'} User
          <IconButton size="small" onClick={() => setConfirmDialog({ open: false, user: null, action: '' })} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          <Typography variant="body2" sx={{ color: isDark ? '#E2E8F0' : 'text.primary' }}>
            Are you sure you want to {confirmDialog.action}{' '}
            <strong>{confirmDialog.user?.firstName} {confirmDialog.user?.lastName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setConfirmDialog({ open: false, user: null, action: '' })} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>Cancel</Button>
          <Button onClick={handleAction} color={confirmDialog.action === 'suspend' ? 'error' : 'success'} variant="contained">
            {confirmDialog.action === 'suspend' ? 'Suspend' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Departments & Rooms View ─────────────────────────────────────────────────
function DepartmentsRoomsView({ facility }) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const cardBorder = `1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'}`;
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';

  const [tab, setTab] = useState('departments');
  const [departments, setDepartments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, type: '', item: null });
  const [form, setForm] = useState({});

  const facilityId = facility?._id || facility;

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const [deptRes, roomRes] = await Promise.all([
        departmentService.list({ facility: facilityId }),
        roomService.list({ facility: facilityId }),
      ]);
      setDepartments(deptRes.data?.departments || []);
      setRooms(roomRes.data?.rooms || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (type) => {
    setForm({});
    setDialog({ open: true, type, item: null });
  };
  const openEdit = (type, item) => {
    setForm({ ...item });
    setDialog({ open: true, type, item });
  };

  const handleSave = async () => {
    try {
      if (dialog.type === 'department') {
        if (dialog.item) {
          await departmentService.update(dialog.item._id, form);
        } else {
          await departmentService.create({ ...form, facility: facilityId });
        }
      } else {
        if (dialog.item) {
          await roomService.update(dialog.item._id, form);
        } else {
          await roomService.create({ ...form, facility: facilityId });
        }
      }
      setDialog({ open: false, type: '', item: null });
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Deactivate this item?')) return;
    try {
      if (type === 'department') await departmentService.delete(id);
      else await roomService.delete(id);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const MODALITIES = ['CR', 'CT', 'MRI', 'US', 'DX', 'MG', 'NM', 'PT', 'XA', 'RF', 'OT'];

  return (
    <Box>
      <Box mb={1.5}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', lineHeight: 1.2, fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
          Departments & Rooms
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', fontFamily: '"Satoshi", sans-serif', color: textColorSecondary }}>
          Configure hospital departments, imaging suites, and modality equipment
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif' }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant={tab === 'departments' ? 'contained' : 'outlined'}
            onClick={() => setTab('departments')}
            sx={{
              height: 32,
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '0.78rem',
              fontWeight: 600,
              fontFamily: '"Satoshi", sans-serif',
              ...(tab !== 'departments' && { borderColor, color: textColorPrimary }),
            }}
          >
            Departments
          </Button>
          <Button
            size="small"
            variant={tab === 'rooms' ? 'contained' : 'outlined'}
            onClick={() => setTab('rooms')}
            sx={{
              height: 32,
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '0.78rem',
              fontWeight: 600,
              fontFamily: '"Satoshi", sans-serif',
              ...(tab !== 'rooms' && { borderColor, color: textColorPrimary }),
            }}
          >
            Rooms / Equipment
          </Button>
        </Box>

        <Button
          startIcon={<Add sx={{ fontSize: 16 }} />}
          variant="contained"
          size="small"
          onClick={() => openCreate(tab === 'departments' ? 'department' : 'room')}
          sx={{
            height: 32,
            borderRadius: '6px',
            textTransform: 'none',
            fontSize: '0.78rem',
            fontWeight: 600,
            fontFamily: '"Satoshi", sans-serif',
          }}
        >
          {tab === 'departments' ? 'Add Department' : 'Add Room'}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
      ) : tab === 'departments' ? (
        <Card sx={{ background: cardBg, borderRadius: '12px', border: cardBorder, boxShadow: 'none' }}>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departments.map((d) => (
                  <TableRow key={d._id} hover>
                    <TableCell sx={{ py: 0.75 }}><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: textColorSecondary }}>{d.departmentId}</Typography></TableCell>
                    <TableCell sx={{ py: 0.75 }}><Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>{d.name}</Typography></TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit('department', d)}><Edit sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Deactivate">
                        <IconButton size="small" color="error" onClick={() => handleDelete('department', d._id)}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {departments.length === 0 && (
                  <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: textColorSecondary, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif' }}>No departments yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        <Card sx={{ background: cardBg, borderRadius: '12px', border: cardBorder, boxShadow: 'none' }}>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }}>Modality</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }}>Notes</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif', py: 0.75, color: textColorSecondary }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rooms.map((r) => (
                  <TableRow key={r._id} hover>
                    <TableCell sx={{ py: 0.75 }}><Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>{r.name}</Typography></TableCell>
                    <TableCell sx={{ py: 0.75 }}><Chip label={r.modality} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }} /></TableCell>
                    <TableCell sx={{ py: 0.75 }}><Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif' }}>{r.notes || '—'}</Typography></TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit('room', r)}><Edit sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                      <Tooltip title="Deactivate">
                        <IconButton size="small" color="error" onClick={() => handleDelete('room', r._id)}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {rooms.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: textColorSecondary, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif' }}>No rooms yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, type: '', item: null })}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '460px' },
            maxWidth: '480px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#F1F5F9' : '#0F172A',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider', fontSize: '1.05rem', fontWeight: 700, fontFamily: '"Satoshi", sans-serif' }}>
          {dialog.item ? 'Edit' : 'Add'} {dialog.type === 'department' ? 'Department' : 'Room'}
          <IconButton size="small" onClick={() => setDialog({ open: false, type: '', item: null })} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 2.5, py: 2, overflowY: 'auto' }}>
          {dialog.type === 'department' ? (
            <>
              <TextField
                label="Department ID" size="small" fullWidth required
                value={form.departmentId || ''}
                onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
                InputProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem', borderRadius: '8px' } }}
                InputLabelProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
              />
              <TextField
                label="Name" size="small" fullWidth required
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                InputProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem', borderRadius: '8px' } }}
                InputLabelProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
              />
            </>
          ) : (
            <>
              <TextField
                label="Room Name" size="small" fullWidth required
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. CT Suite 1"
                InputProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem', borderRadius: '8px' } }}
                InputLabelProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}>Modality</InputLabel>
                <Select
                  label="Modality"
                  value={form.modality || ''}
                  onChange={(e) => setForm({ ...form, modality: e.target.value })}
                  sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem', borderRadius: '8px' }}
                >
                  {MODALITIES.map((m) => <MenuItem key={m} value={m} sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}>{m}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                label="Notes (optional)" size="small" fullWidth multiline rows={2} maxRows={3}
                value={form.notes || ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                InputProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem', borderRadius: '8px' } }}
                InputLabelProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setDialog({ open: false, type: '', item: null })} sx={{ color: isDark ? '#94A3B8' : '#64748B', fontFamily: '"Satoshi", sans-serif', fontSize: '0.78rem', textTransform: 'none', borderRadius: '6px' }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" size="small" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.78rem', textTransform: 'none', borderRadius: '6px', fontWeight: 600 }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Analytics View ───────────────────────────────────────────────────────────
function AnalyticsView({ facility }) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const cardBorder = `1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'}`;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const facilityId = facility?._id || facility;

  useEffect(() => {
    if (!facilityId) return;
    orderService.getStats({ facility: facilityId })
      .then((res) => {
        setStats(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [facilityId]);

  const statusColors = {
    NEW: '#3b82f6',
    ARRIVAL: '#8b5cf6',
    READY: '#10b981',
    PENDING_REPORT: '#f59e0b',
    REPORTED: '#6366f1',
    CANCELLED: '#ef4444',
  };

  return (
    <Box>
      <Box mb={1.5}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', lineHeight: 1.2, fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
          Analytics & Reports
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', fontFamily: '"Satoshi", sans-serif', color: textColorSecondary }}>
          Facility imaging volume, order distributions, and modality breakdown
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
      ) : stats ? (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, borderRadius: '12px', border: cardBorder, background: cardBg, boxShadow: 'none' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.85rem', fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
                Orders by Status
              </Typography>
              {stats.byStatus ? Object.entries(stats.byStatus).map(([status, count]) => (
                <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColors[status] || '#94a3b8', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
                    {status}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
                    {count}
                  </Typography>
                </Box>
              )) : (
                <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif' }}>
                  No data
                </Typography>
              )}
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, borderRadius: '12px', border: cardBorder, background: cardBg, boxShadow: 'none' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.85rem', fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
                Orders by Modality
              </Typography>
              {stats.byModality ? Object.entries(stats.byModality).map(([mod, count]) => (
                <Box key={mod} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Chip label={mod} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }} />
                  <Box sx={{ flex: 1, height: 6, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%', borderRadius: 3, bgcolor: '#3b82f6',
                      width: `${Math.min(100, (count / (stats.total || 1)) * 100)}%`,
                    }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 24, textAlign: 'right', fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
                    {count}
                  </Typography>
                </Box>
              )) : (
                <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.78rem', fontFamily: '"Satoshi", sans-serif' }}>
                  No data
                </Typography>
              )}
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info" sx={{ py: 0.5, borderRadius: '8px', fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif' }}>
          No analytics data available yet.
        </Alert>
      )}
    </Box>
  );
}

// ─── Main Facility Admin Dashboard ────────────────────────────────────────────
export default function FacilityAdminDashboard() {
  const { user, logout } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';
  const pageBg = isDark ? '#121212' : '#F8FAFC';

  const isMobile = useMediaQuery('(max-width:768px)');
  const [activeSection, setActiveSection] = useState('overview');
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);

  const theme = createTheme({
    palette: { mode: mode || 'light' },
  });

  const clinicalRole = user?.clinicalRole;
  const isClinician = clinicalRole === 'clinician';
  const isRadiologist = clinicalRole === 'radiologist';

  const managementNav = [
    {
      key: 'overview', label: 'Overview',
      icon: <DashboardIcon sx={{ fontSize: 18 }} />,
    },
    {
      key: 'staff', label: 'Staff Management',
      icon: <Group sx={{ fontSize: 18 }} />,
    },
    {
      key: 'departments', label: 'Departments & Rooms',
      icon: <Apartment sx={{ fontSize: 18 }} />,
    },
    {
      key: 'analytics', label: 'Analytics',
      icon: <BarChart sx={{ fontSize: 18 }} />,
    },
  ];

  const clinicalNav = isClinician ? [
    { key: 'clinical', label: 'Clinical Dashboard', icon: <Assignment sx={{ fontSize: 18 }} /> },
  ] : isRadiologist ? [
    { key: 'clinical', label: 'Radiologist Portal', icon: <Assignment sx={{ fontSize: 18 }} /> },
  ] : [];

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
  };

  const renderContent = () => {
    if (activeSection === 'clinical') {
      if (isClinician) return <ClinicianDashboard />;
      if (isRadiologist) return <RadiologistDashboard />;
      return null;
    }
    const facility = user?.facility;
    switch (activeSection) {
      case 'overview': return <OverviewView facility={facility} />;
      case 'staff': return <StaffView facility={facility} />;
      case 'departments': return <DepartmentsRoomsView facility={facility} />;
      case 'analytics': return <AnalyticsView facility={facility} />;
      default: return <OverviewView facility={facility} />;
    }
  };

  const isClinicalView = activeSection === 'clinical';

  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Brand */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Typography variant="subtitle2" sx={{ color: '#94a3b8', fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', mb: 0.5, fontFamily: '"Satoshi", sans-serif' }}>
          Facility Admin
        </Typography>
        <Typography variant="body1" sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: 13, lineHeight: 1.3, fontFamily: '"Satoshi", sans-serif' }}>
          {user?.facility?.facilityName || 'My Facility'}
        </Typography>
        <Chip
          label={isClinician ? 'Clinician' : isRadiologist ? 'Radiologist' : 'Admin'}
          size="small"
          sx={{ mt: 1, bgcolor: '#1e40af', color: '#bfdbfe', fontSize: 10, height: 20, fontFamily: '"Satoshi", sans-serif', fontWeight: 600 }}
        />
      </Box>

      {/* Management section */}
      <Box sx={{ pt: 1 }}>
        <Typography sx={{ px: 2, py: 0.75, fontSize: 10, letterSpacing: 1.5, color: '#475569', textTransform: 'uppercase', fontFamily: '"Satoshi", sans-serif', fontWeight: 700 }}>
          Facility Management
        </Typography>
        <List dense disablePadding>
          {managementNav.map((item) => {
            const active = activeSection === item.key;
            return (
              <ListItemButton
                key={item.key}
                onClick={() => { setActiveSection(item.key); if (isMobile) setDrawerOpen(false); }}
                sx={{
                  mx: 1, mb: 0.5, borderRadius: '8px', py: 0.6,
                  bgcolor: active ? 'rgba(59,130,246,0.15)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: active ? '#60a5fa' : '#94a3b8' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? '#e2e8f0' : '#94a3b8', fontFamily: '"Satoshi", sans-serif' }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Clinical section */}
      {clinicalNav.length > 0 && (
        <Box sx={{ pt: 1 }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2, mb: 1 }} />
          <Typography sx={{ px: 2, py: 0.75, fontSize: 10, letterSpacing: 1.5, color: '#475569', textTransform: 'uppercase', fontFamily: '"Satoshi", sans-serif', fontWeight: 700 }}>
            Clinical Work
          </Typography>
          <List dense disablePadding>
            {clinicalNav.map((item) => {
              const active = activeSection === item.key;
              return (
                <ListItemButton
                  key={item.key}
                  onClick={() => { setActiveSection(item.key); if (isMobile) setDrawerOpen(false); }}
                  sx={{
                    mx: 1, mb: 0.5, borderRadius: '8px', py: 0.6,
                    bgcolor: active ? 'rgba(16,185,129,0.15)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: active ? '#34d399' : '#94a3b8' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? '#e2e8f0' : '#94a3b8', fontFamily: '"Satoshi", sans-serif' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {/* User footer */}
      <Box sx={{ p: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar sx={{ width: 30, height: 30, bgcolor: '#1e40af', fontSize: 12, fontFamily: '"Satoshi", sans-serif' }}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: '#e2e8f0', fontSize: 12, fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: '"Satoshi", sans-serif' }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 10, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: '"Satoshi", sans-serif' }}>
              {user?.email}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleLogout} sx={{ color: '#64748b', '&:hover': { color: '#ef4444' } }}>
            <LogoutIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  // When showing the full clinical dashboard (ClinicianDashboard / RadiologistDashboard),
  // render it full-screen — they have their own layout/topbar.
  if (isClinicalView) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'fixed', top: 12, left: 12, zIndex: 9999 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setActiveSection('overview')}
              sx={{ bgcolor: '#0a1929', color: '#e2e8f0', '&:hover': { bgcolor: '#1e3a5f' }, borderRadius: '8px', fontSize: 12, textTransform: 'none', fontFamily: '"Satoshi", sans-serif' }}
              startIcon={<DashboardIcon sx={{ fontSize: 16 }} />}
            >
              Back to Facility Admin
            </Button>
          </Box>
          {renderContent()}
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: pageBg }}>
        {/* Sidebar */}
        {isMobile ? (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { width: DRAWER_WIDTH, bgcolor: '#0a1929' } }}
          >
            {sidebarContent}
          </Drawer>
        ) : (
          <StyledDrawer variant="permanent" open>
            {sidebarContent}
          </StyledDrawer>
        )}

        {/* Main content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Top bar */}
          <AppBar
            position="static"
            elevation={0}
            sx={{ bgcolor: cardBg, borderBottom: `1px solid ${borderColor}`, color: textColorPrimary }}
          >
            <Toolbar sx={{ gap: 1, minHeight: 52, px: { xs: 2, sm: 2.5 } }}>
              {isMobile && (
                <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: textColorSecondary }}>
                  <MenuIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem', flex: 1, fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
                {managementNav.find(n => n.key === activeSection)?.label || clinicalNav.find(n => n.key === activeSection)?.label || 'Dashboard'}
              </Typography>
              <Chip
                label={user?.facility?.facilityName || 'No facility'}
                size="small"
                icon={<Business sx={{ fontSize: 13 }} />}
                sx={{
                  bgcolor: isDark ? 'rgba(59,130,246,0.15)' : '#eff6ff',
                  color: isDark ? '#93c5fd' : '#1d4ed8',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  fontFamily: '"Satoshi", sans-serif',
                  height: 24,
                }}
              />
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: textColorSecondary, p: 0.5 }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: '#1e40af', fontSize: 11, fontFamily: '"Satoshi", sans-serif' }}>
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </Avatar>
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem disabled>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif' }}>{user?.firstName} {user?.lastName}</Typography>
                    <Typography variant="caption" sx={{ color: textColorSecondary, fontSize: '0.7rem', fontFamily: '"Satoshi", sans-serif' }}>Facility Admin</Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ fontSize: '0.8rem', fontFamily: '"Satoshi", sans-serif' }}><LogoutIcon sx={{ mr: 1, fontSize: 16 }} /> Logout</MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>

          {/* Page content */}
          <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5 }, overflow: 'auto' }}>
            <Box sx={{ maxWidth: 1100 }}>
              {renderContent()}
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
